'use client';

import { useState, useEffect, useRef, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/context/AuthContext';
import {
  Mail, Lock, User, Eye, EyeOff, Loader2,
  ArrowRight, ArrowLeft, CheckCircle, CheckCircle2, XCircle, Gift, Info,
} from 'lucide-react';
import { AuthLayout, FormInput, SocialLogin, ErrorAlert } from '@/components/auth';
import { referralApi, isDefinitiveReferralRejection } from '@/lib/api/referral';
import { useTranslations } from 'next-intl';
import { getRedirectUrl } from '@/lib/redirect-url';
import { rememberPostAuthPath } from '@/lib/post-auth-redirect';
import { safeSession } from '@/lib/safe-storage';
import { isMarketingAdult, isValidBirthYear } from '@/lib/consent-age';
import { BirthYearInput } from '@/components/forms/BirthYearInput';

const API_BASE_URL = '/api/proxy';
const DASHBOARD_URL = process.env.NEXT_PUBLIC_DASHBOARD_URL || '';
// sessionStorage fallback for the post-signup redirect target — mirrors
// 'pendingReferralCode' below. Firebase's signInWithRedirect fallback (see
// isStandaloneOrInAppBrowser in AuthContext) does a full top-level navigation away from
// this page and back; the return trip is expected to preserve the original URL's query
// string, but that isn't guaranteed across every in-app browser/webview, so this survives
// independently of the URL.
const PENDING_REDIRECT_KEY = 'pendingRedirectTarget';

// Every sessionStorage touch on this page goes through `safeSession` (see
// @/lib/safe-storage for why the guard must wrap the property dereference, not just
// the getItem call). These three local wrappers used to live here and were the only
// correct storage handling in the app; they are now the shared helper, which is what
// the rest of the dashboard uses too.
//
// Degrading is safe: the redirect target and referral code both have a live query-param
// path, and the stash is only a fallback for a signInWithRedirect round-trip.
const safeSessionGet = (key: string) => safeSession.get(key);
const safeSessionSet = (key: string, value: string) => void safeSession.set(key, value);
const safeSessionRemove = (key: string) => void safeSession.remove(key);

// Firebase Auth throws errors shaped as `{ code: string, message: string }`.
// Narrow the unknown catch value down to that `code` without resorting to `any`.
function getFirebaseErrorCode(err: unknown): string {
  if (err && typeof err === 'object' && 'code' in err) {
    return String((err as { code: unknown }).code);
  }
  return '';
}

function RegisterPageInner() {
  const t = useTranslations('auth');
  const tc = useTranslations('common');
  const searchParams = useSearchParams();
  const { registerWithEmail, loginWithGoogle, redirectSignInCompleted, redirectSignInErrorCode } = useAuth();

  // Prefer the live query param; fall back to the sessionStorage stash (see
  // PENDING_REDIRECT_KEY above) for the case where a signInWithRedirect round-trip lost it.
  // Both pass through getRedirectUrl so a stashed value can never smuggle in something the
  // live-param path would have rejected.
  const redirectTo = (() => {
    const fromQuery = getRedirectUrl(searchParams.get('redirect'));
    if (fromQuery !== '/') return fromQuery;
    if (typeof window === 'undefined') return '/';
    return getRedirectUrl(safeSessionGet(PENDING_REDIRECT_KEY));
  })();
  // Forward the target through to /login too — a user who lands here but already has an
  // account (see the "email already in use" case below) must not lose it by following
  // "login here".
  const loginHref = redirectTo !== '/' ? `/login?redirect=${encodeURIComponent(redirectTo)}` : '/login';

  const [registrationEnabled, setRegistrationEnabled] = useState<boolean | null>(null);
  const [registrationMessage, setRegistrationMessage] = useState('');
  // Email/password signup is pulled from `providers` when the Resend key isn't
  // configured (see auth.controller.ts getRegistrationStatus) — Google stays open.
  // Missing field (older API shape) or a failed fetch fails open, same as `enabled`.
  const [emailSignupAvailable, setEmailSignupAvailable] = useState(true);
  const [step, setStep] = useState(1);
  // Google is the primary path on this screen (auto-verified, one click — see
  // OnboardingGuard/CartDrawer's checkout verification gate for why that matters).
  // Email/password stays fully available but starts collapsed behind the divider below.
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  // Signup-consent capture (fast-follow): opt-in, unchecked by default, and never
  // required to proceed — only agreeTerms gates submission (see handleStep1 below).
  const [agreeMarketing, setAgreeMarketing] = useState(false);
  // Revealed only when agreeMarketing is ticked (2026-09-28 marketing-consent
  // plan — age is asked ONLY where consent is asked). Kept as a raw string so
  // the input can be genuinely empty rather than coerced to 0.
  const [birthYearInput, setBirthYearInput] = useState('');
  const [underageNotice, setUnderageNotice] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Referral code state
  const [showReferralInput, setShowReferralInput] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referralStatus, setReferralStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  const [referralName, setReferralName] = useState('');
  const referralDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/auth/registration-status`)
      .then((r) => r.json())
      .then((data) => {
        setRegistrationEnabled(data.enabled !== false);
        if (data.message) setRegistrationMessage(data.message);
        if (Array.isArray(data.providers)) {
          setEmailSignupAvailable(data.providers.includes('email'));
        }
      })
      .catch(() => setRegistrationEnabled(true)); // fail open
  }, []);

  const validatePassword = (pass: string) => ({
    minLength: pass.length >= 8,
    hasNumber: /\d/.test(pass),
    hasLetter: /[a-zA-Z]/.test(pass),
  });

  const passwordStrength = validatePassword(password);
  const isPasswordValid = Object.values(passwordStrength).every(Boolean);

  // Required only when the marketing checkbox is ticked — birth year is
  // never a mandatory field on its own (2026-09-28 plan, decision 2).
  const currentYear = new Date().getFullYear();
  const parsedBirthYear = isValidBirthYear(birthYearInput, currentYear) ? Number(birthYearInput) : undefined;
  const isBirthYearValid = parsedBirthYear !== undefined;
  // Client-side pre-check (mirrors is-adult.ts's threshold via consent-age.ts)
  // — the moment the revealed birth-year field resolves to under
  // MARKETING_MIN_AGE, the checkbox is unticked below so a grant is never
  // submitted in this state. Kept visible off `agreeMarketing` alone would
  // hide this notice the instant the untick fires, so the reveal condition
  // below also checks this flag directly.
  const isBirthYearUnderage = parsedBirthYear !== undefined && !isMarketingAdult(parsedBirthYear, currentYear);
  const showConsentDetails = agreeMarketing || isBirthYearUnderage;

  const handleStep1 = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) { setError(t('passwordMismatchError')); return; }
    if (!isPasswordValid) { setError(t('passwordInvalidError')); return; }
    if (!agreeTerms) { setError(t('agreeTermsError')); return; }
    if (agreeMarketing && !isBirthYearValid) { setError(t('consent.birthYearRequiredError')); return; }
    setStep(2);
  };

  const handleReferralCodeChange = (value: string) => {
    const upper = value.toUpperCase();
    setReferralCode(upper);
    setReferralStatus('idle');
    setReferralName('');

    if (referralDebounce.current) clearTimeout(referralDebounce.current);
    if (upper.length < 4) return;

    referralDebounce.current = setTimeout(async () => {
      setReferralStatus('validating');
      try {
        const result = await referralApi.validate(upper);
        if (result.valid) {
          setReferralStatus('valid');
          setReferralName(result.referrerName || '');
        } else {
          setReferralStatus('invalid');
        }
      } catch {
        setReferralStatus('invalid');
      }
    }, 600);
  };

  useEffect(() => {
    const refParam = searchParams?.get('ref')?.toUpperCase().trim();
    if (refParam) {
      safeSessionSet('pendingReferralCode', refParam);
      setShowReferralInput(true);
      setReferralCode(refParam);
      handleReferralCodeChange(refParam); // existing debounced validator
    } else {
      // Organic visit (no ref) — clear any code left over from an abandoned
      // earlier ?ref= visit so IdentityStep's sessionStorage fallback can't
      // misattribute this signup to a stale referrer.
      safeSessionRemove('pendingReferralCode');
    }

    // Same idea for ?redirect= (see PENDING_REDIRECT_KEY / FIX 1): stash the validated
    // target so a signInWithRedirect round-trip can recover it even if the return URL's
    // query string doesn't make it back intact. Cleared on an organic visit for the same
    // stale-value reason as pendingReferralCode above.
    const validatedRedirect = getRedirectUrl(searchParams.get('redirect'));
    if (validatedRedirect !== '/') {
      safeSessionSet(PENDING_REDIRECT_KEY, validatedRedirect);
      // Also kept for the verification email's landing page, which opens in another tab.
      rememberPostAuthPath(validatedRedirect);
    } else {
      safeSessionRemove(PENDING_REDIRECT_KEY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Navigates to wherever the user was headed (default: dashboard home). Uses a full
  // navigation, not the Next router, because redirectTo may be an absolute cross-origin
  // URL (see getRedirectUrl's TRUSTED_ORIGINS) that router.push cannot follow.
  const navigateToRedirectTarget = () => {
    safeSessionRemove(PENDING_REDIRECT_KEY);
    window.location.href = redirectTo.startsWith('http') ? redirectTo : `${DASHBOARD_URL}${redirectTo}`;
  };

  // Apply a validated referral code to the just-created account. Shared by
  // both the email/password and Google sign-up paths — the Google path used
  // to skip this entirely, silently dropping the referral for every social
  // signup. Fire-and-forget: never blocks or surfaces as a signup failure.
  // Only clears the sessionStorage fallback on success or a definitive
  // server-side rejection (bad code / self-referral / already-referred /
  // apply-window-closed) — a transient failure (network blip, 5xx) leaves
  // the code in place so IdentityStep's onboarding-step fallback can retry.
  const applyPendingReferral = () => {
    if (!referralCode || referralStatus !== 'valid') return;
    referralApi
      .apply(referralCode, 'CODE')
      .then(() => safeSessionRemove('pendingReferralCode'))
      .catch((err: unknown) => {
        if (isDefinitiveReferralRejection(err)) {
          safeSessionRemove('pendingReferralCode');
        } else {
          console.error('Referral apply failed, will retry during onboarding:', err);
        }
      });
  };

  const handleRegister = async () => {
    setError('');
    setIsSubmitting(true);
    try {
      const result = await registerWithEmail(
        email,
        password,
        name,
        agreeMarketing,
        agreeMarketing ? parsedBirthYear : undefined,
        agreeTerms,
      );
      applyPendingReferral();
      // The account was created successfully either way — a NOT_ADULT refusal
      // only means the marketing-consent grant itself was declined (see
      // AuthContext.registerWithEmail). Show that once instead of silently
      // dropping it, then continue on to the dashboard as normal.
      if (result.marketingConsentRefusalReason === 'NOT_ADULT') {
        setUnderageNotice(true);
        setIsSubmitting(false);
        return;
      }
      navigateToRedirectTarget();
    } catch (err: unknown) {
      setError(getErrorMessage(getFirebaseErrorCode(err)));
      setStep(1);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setIsSubmitting(true);
    try {
      await loginWithGoogle();
      // Popup path only: on the redirect fallback (isStandaloneOrInAppBrowser), this
      // promise never resolves here — the page navigates away first. That case is
      // completed by the redirectSignInCompleted effect below instead.
      applyPendingReferral();
      navigateToRedirectTarget();
    } catch (err: unknown) {
      setError(getErrorMessage(getFirebaseErrorCode(err)));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getErrorMessage = (code: string) => {
    switch (code) {
      case 'auth/email-already-in-use': return t('errorEmailInUse');
      case 'auth/invalid-email': return t('errorInvalidEmail');
      case 'auth/weak-password': return t('errorWeakPassword');
      case 'auth/popup-closed-by-user': return t('errorRegisterCancelled');
      // Thrown by AuthService.syncUser (auth.service.ts) via registerWithEmail — same
      // `code` shape as a Firebase error, so it flows through getFirebaseErrorCode() above
      // unchanged. Reuses the login page's copy for these two rather than inventing new
      // strings for the same situation.
      case 'REGISTRATION_DISABLED': return t('registrationDisabled');
      case 'EMAIL_SIGNUP_UNAVAILABLE': return t('emailSignupUnavailable');
      default: return t('errorGeneral');
    }
  };

  // Mirrors the login page's identical effect: completes a signInWithRedirect flow that
  // had to leave this page (isStandaloneOrInAppBrowser, see AuthContext) and can only be
  // picked up on this page's own next mount. Without this, a redirect-based Google
  // sign-up leaves the user authenticated but stranded on /register.
  useEffect(() => {
    if (redirectSignInCompleted) {
      applyPendingReferral();
      navigateToRedirectTarget();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectSignInCompleted]);

  useEffect(() => {
    if (redirectSignInErrorCode) {
      setError(getErrorMessage(redirectSignInErrorCode));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectSignInErrorCode]);

  const passwordToggle = (
    <button
      type="button"
      onClick={() => setShowPassword(!showPassword)}
      disabled={isSubmitting}
      className="text-slate-400 hover:text-slate-600 transition-colors"
    >
      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
    </button>
  );

  if (registrationEnabled === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  if (!registrationEnabled) {
    return (
      <AuthLayout title={t('registrationClosed')} subtitle="">
        <div className="flex flex-col items-center text-center gap-5 py-4">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <p className="text-sm text-slate-600">{registrationMessage || t('registrationDisabled')}</p>
          <Link
            href="/login"
            className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm bg-orange-500 hover:bg-orange-600 text-white transition-all"
          >
            {t('loginToAccount')}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={step === 1 ? t('createAccountTitle') : t('confirmDataTitle')}
      subtitle={step === 1 ? t('createAccountSubtitle') : t('confirmDataSubtitle')}
    >
      {/* Steps indicator */}
      <div className="flex items-center justify-center gap-3 mb-8">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
          step >= 1 ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-400'
        }`}>1</div>
        <div className={`w-12 h-1 rounded-full transition-colors ${step >= 2 ? 'bg-orange-500' : 'bg-slate-200'}`} />
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
          step >= 2 ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-400'
        }`}>2</div>
      </div>

      <ErrorAlert message={error} />

      {step === 1 ? (
        <>
          <SocialLogin
            onGoogleClick={handleGoogleLogin}
            isLoading={isSubmitting}
            divider="none"
            helperText={t('googleHelperText')}
          />

          {!emailSignupAvailable && (
            <div className="mt-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">{t('emailSignupUnavailable')}</p>
            </div>
          )}

          {emailSignupAvailable && (
          <>
          {showEmailForm ? (
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-4 text-xs text-slate-400">{t('socialDividerEmail')}</span>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowEmailForm(true)}
              className="relative my-6 w-full group"
            >
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200 group-hover:border-slate-300 transition-colors" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-4 text-xs text-slate-400 group-hover:text-teal-600 transition-colors">
                  {t('socialDividerEmail')}
                </span>
              </div>
            </button>
          )}

          {showEmailForm && (
          <form onSubmit={handleStep1} className="space-y-4">
            <FormInput
              label={t('fullNameLabel')}
              icon={User}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('fullNamePlaceholder')}
              required
              disabled={isSubmitting}
            />

            <FormInput
              label={t('emailLabel')}
              icon={Mail}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nama@email.com"
              required
              disabled={isSubmitting}
              autoComplete="email"
            />

            <div>
              <FormInput
                label={t('passwordLabel')}
                icon={Lock}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('passwordMinPlaceholder')}
                required
                disabled={isSubmitting}
                rightElement={passwordToggle}
              />
              {password && (
                <div className="mt-2 space-y-1">
                  {[
                    { ok: passwordStrength.minLength, label: t('passwordMinRule') },
                    { ok: passwordStrength.hasLetter, label: t('passwordLetterRule') },
                    { ok: passwordStrength.hasNumber, label: t('passwordNumberRule') },
                  ].map(({ ok, label }) => (
                    <div key={label} className="flex items-center gap-2 text-xs">
                      {ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <XCircle className="w-3.5 h-3.5 text-slate-300" />}
                      <span className={ok ? 'text-emerald-600' : 'text-slate-400'}>{label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <FormInput
              label={t('confirmPasswordLabel')}
              icon={Lock}
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('confirmPasswordPlaceholder')}
              required
              disabled={isSubmitting}
              error={confirmPassword && password !== confirmPassword ? t('confirmPasswordMismatch') : undefined}
            />

            {/* Referral code — collapsible */}
            <div>
              {!showReferralInput ? (
                <button
                  type="button"
                  onClick={() => setShowReferralInput(true)}
                  className="text-xs text-slate-500 hover:text-teal-600 transition-colors flex items-center gap-1"
                >
                  <Gift className="w-3.5 h-3.5" />
                  {t('hasReferralCode')}
                </button>
              ) : (
                <div className="space-y-1.5">
                  <label className="block text-xs font-medium text-slate-600">
                    {t('referralCodeLabel')} <span className="text-slate-400">{t('referralCodeOptional')}</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={referralCode}
                      onChange={(e) => handleReferralCodeChange(e.target.value)}
                      placeholder={t('referralCodePlaceholder')}
                      maxLength={20}
                      disabled={isSubmitting}
                      className="w-full px-3 py-2.5 pr-9 border border-slate-200 rounded-lg text-sm font-mono text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-colors placeholder:text-slate-400 placeholder:font-sans"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {referralStatus === 'validating' && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
                      {referralStatus === 'valid' && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                      {referralStatus === 'invalid' && <XCircle className="w-4 h-4 text-red-400" />}
                    </div>
                  </div>
                  {referralStatus === 'valid' && referralName && (
                    <p className="text-xs text-emerald-600">{t('referralCodeValidFrom', { name: referralName })}</p>
                  )}
                  {referralStatus === 'invalid' && (
                    <p className="text-xs text-red-500">{t('referralCodeInvalid')}</p>
                  )}
                </div>
              )}
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-slate-300 accent-teal-600 focus:ring-teal-500 cursor-pointer"
                disabled={isSubmitting}
              />
              <span className="text-sm text-slate-600 leading-relaxed">
                {t('agreeTermsText')}{' '}
                <Link href="#" className="text-teal-600 hover:text-teal-700 font-medium">{t('termsLink')}</Link>
                {' '}{t('and')}{' '}
                <Link href="#" className="text-teal-600 hover:text-teal-700 font-medium">{t('privacyLink')}</Link>
              </span>
            </label>

            <div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreeMarketing}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    // Guard the manual path too — a still-visible under-18 year
                    // must not let a re-click force the grant back on.
                    if (checked && isBirthYearUnderage) return;
                    setAgreeMarketing(checked);
                    if (!checked) setBirthYearInput('');
                  }}
                  className="mt-0.5 w-4 h-4 rounded border-slate-300 accent-teal-600 focus:ring-teal-500 cursor-pointer"
                  disabled={isSubmitting}
                />
                <span className="text-sm text-slate-600 leading-relaxed">
                  {t('agreeMarketingText')}
                </span>
              </label>

              {/* Revealed once the marketing checkbox is ticked — age is asked
                  ONLY where consent is asked (2026-09-28 plan, decision 2).
                  Stays visible after an auto-untick below so the inline
                  under-18 notice has somewhere to render. */}
              {showConsentDetails && (
                <div className="mt-2 ml-7">
                  <BirthYearInput
                    id="register-birth-year-input"
                    value={birthYearInput}
                    onChange={(digits) => {
                      setBirthYearInput(digits);
                      const parsed = isValidBirthYear(digits, currentYear) ? Number(digits) : undefined;
                      // Pre-submit, client-side refusal (2026-09-28 plan, item 3):
                      // the moment the typed year resolves under
                      // MARKETING_MIN_AGE, untick the checkbox right here — no
                      // grant is ever submitted for this state. The server
                      // (writeMarketingConsent) still enforces the same rule
                      // as defense in depth, but this is the primary UX.
                      if (parsed !== undefined && !isMarketingAdult(parsed, currentYear)) {
                        setAgreeMarketing(false);
                      }
                    }}
                    label={t('consent.birthYearLabel')}
                    placeholder={t('consent.birthYearPlaceholder')}
                    disabled={isSubmitting}
                    labelClassName="block text-xs font-medium text-slate-600"
                    inputClassName="border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                  />
                  {isBirthYearUnderage ? (
                    <p data-testid="marketing-consent-underage-inline-notice" className="mt-1 text-xs text-amber-700">
                      {t('consent.notAvailableForAge')}
                    </p>
                  ) : (
                    <p className="mt-1 text-[11px] text-slate-400">{t('consent.birthYearHint')}</p>
                  )}
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !isPasswordValid}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm bg-orange-500 hover:bg-orange-600 text-white shadow-sm hover:shadow active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('continueBtn')} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
          )}
          </>
          )}

          <p className="mt-8 text-center text-sm text-slate-500">
            {t('alreadyHaveAccount')}{' '}
            <Link href={loginHref} className="font-semibold text-teal-600 hover:text-teal-700 transition-colors">
              {t('loginHere')}
            </Link>
          </p>
        </>
      ) : (
        <div className="space-y-6">
          <div className="bg-emerald-500 rounded-xl p-4 text-white">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                <CheckCircle className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">{t('dataVerifiedTitle')}</h3>
                <p className="text-xs text-white/80">{t('dataVerifiedSubtitle')}</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
              <p className="text-[11px] font-medium text-slate-600 uppercase tracking-wide mb-0.5">{t('fullNameLabel')}</p>
              <p className="text-sm font-semibold text-slate-900">{name}</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
              <p className="text-[11px] font-medium text-slate-600 uppercase tracking-wide mb-0.5">{t('emailLabel')}</p>
              <p className="text-sm font-semibold text-slate-900">{email}</p>
            </div>
          </div>

          <div className="bg-teal-50 rounded-xl p-4">
            <p className="text-sm font-semibold text-teal-900 mb-3">{t('joinBenefitsTitle')}</p>
            <ul className="space-y-2">
              {[t('benefit1'), t('benefit2'), t('benefit3'), t('benefit4')].map((b) => (
                <li key={b} className="flex items-center gap-2 text-sm text-teal-900">
                  <CheckCircle2 className="w-4 h-4 text-teal-600 shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
          </div>

          {/* Account created, marketing-consent grant refused by the age gate
              (2026-09-28 plan) — see registerWithEmail's marketingConsentRefusalReason. */}
          {underageNotice && (
            <div
              data-testid="marketing-consent-underage-notice"
              className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
            >
              <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">{t('consent.notAvailableForAge')}</p>
            </div>
          )}

          {underageNotice ? (
            <button
              onClick={navigateToRedirectTarget}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm bg-orange-500 hover:bg-orange-600 text-white shadow-sm hover:shadow active:scale-[0.98] transition-all"
            >
              {t('continueBtn')} <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                disabled={isSubmitting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border border-slate-200 rounded-xl font-semibold text-sm text-slate-700 bg-white hover:bg-slate-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowLeft className="w-4 h-4" /> {tc('back')}
              </button>
              <button
                onClick={handleRegister}
                disabled={isSubmitting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm bg-orange-500 hover:bg-orange-600 text-white shadow-sm hover:shadow active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (<>{t('registerBtn')} <ArrowRight className="w-4 h-4" /></>)}
              </button>
            </div>
          )}
        </div>
      )}
    </AuthLayout>
  );
}

export default function RegisterPage() {
  return (
    <Suspense fallback={null}>
      <RegisterPageInner />
    </Suspense>
  );
}
